import fallbackImage from "@assets/images/fallback-game.svg";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {HeadingCarousel} from "@/components";
import {useAuthStore} from "@/store/useAuth.store";
import {avatarImages} from "@/util/avatarUtils";
import useGetGameCarousels from "../../../../../../../hooks/useGetGameCarousels";
import styles from "./game-promos.styles.module.scss";

export const GamePromos = () => {
  const {t} = useTranslation();
  const {promos} = useGetGameCarousels();
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const MOCK_PLAYERS = [
    {name: "Carlos M.", alias: "carlosm", color: "#f59e0b", avatar: avatarImages[0]},
    {name: "Ana R.", alias: "anar", color: "#ec4899", avatar: avatarImages[1]},
    {name: "Luis P.", alias: "luisp", color: "#8b5cf6", avatar: avatarImages[2]},
    {name: "María G.", alias: "mariag", color: "#14b8a6", avatar: avatarImages[3]},
    {name: "Pedro S.", alias: "pedros", color: "#3b82f6", avatar: avatarImages[4]},
    {name: "Sofía L.", alias: "sofial", color: "#ef4444", avatar: avatarImages[0]},
    {name: "Diego V.", alias: "diegov", color: "#22c55e", avatar: avatarImages[1]},
    {name: "Valentina C.", alias: "valentinac", color: "#f97316", avatar: avatarImages[2]},
  ];

  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!activePopup) return;
    const handleClickOutside = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [activePopup]);

  const selectedPlayer = MOCK_PLAYERS.find(p => p.alias === activePopup);

  const handlePlayerClick = useCallback(alias => {
    setActivePopup(prev => (prev === alias ? null : alias));
  }, []);

  const handleGameClick = useCallback(
    game => {
      if (authStatus === "authenticated") {
        navigate(`/games/${game.provider}/${game.provider_game_id}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleImageError = useCallback(e => {
    e.target.onerror = null;
    e.target.src = fallbackImage;
  }, []);

  const handleDocumentationClick = useCallback(url => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  if (!promos.length) {
    return null;
  }

  const heroPromo = promos[0];

  return (
    <section className={styles.promoArena}>
      <HeadingCarousel
        title={t("dontMiss")}
        subtitle={t("dontMissTournaments")}
        icon="🔥"
      />

      <div className={styles.heroCard}>
        <div className={styles.heroAccentLine} />
        <div className={styles.heroGlow} />

        <div className={styles.heroBody}>
          <span className={styles.heroBadge}>
            <span className={styles.heroBadgeDot} />
            {t("promoFeatured")}
          </span>

          <h2 className={styles.heroTitle}>{heroPromo.title}</h2>

          {/* ── Horizontal game carousel with edge fade ── */}
          <div className={styles.heroGamesWrap}>
            <div className={styles.heroGamesTrack}>
              {(heroPromo.games ?? []).slice(0, 8).map(game => (
                <div
                  key={game.id}
                  className={styles.heroGameThumb}
                  onClick={() => handleGameClick(game)}
                  onKeyDown={e => e.key === "Enter" && handleGameClick(game)}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={game.thumbnail_url || fallbackImage}
                    alt={game.name}
                    onError={handleImageError}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatIcon}>💰</span>
              <div className={styles.heroStatInfo}>
                <span className={styles.heroStatLabel}>{t("promoPrizePool")}</span>
                <span className={styles.heroStatValue}>5,000 EUR</span>
              </div>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatIcon}>⏳</span>
              <div className={styles.heroStatInfo}>
                <span className={styles.heroStatLabel}>{t("promoTimeLeft")}</span>
                <span className={styles.heroStatValue}>2d 14h</span>
              </div>
            </div>
            <div className={styles.heroStatDivider} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatIcon}>👥</span>
              <div className={styles.heroStatInfo}>
                <span className={styles.heroStatLabel}>{t("promoPlayers")}</span>
                <span className={styles.heroStatValue}>1,247</span>
              </div>
            </div>
          </div>

          <div className={styles.heroCtas}>
            <button
              type="button"
              className={styles.heroPrimaryBtn}
              onClick={() => handleDocumentationClick(heroPromo.documentation_url)}
            >
              {t("promoLearnMore")}
            </button>
          </div>

          {/* ── Recent players pills ── */}
          <div className={styles.playersWrap}>
            <span className={styles.playersLabel}>
              👥 {t("promoRecentPlayers", {defaultValue: "Últimos jugadores participantes"})}
            </span>
            <div className={styles.playersTrack}>
              {MOCK_PLAYERS.map((player, i) => (
                <button
                  key={player.alias}
                  type="button"
                  className={`${styles.playerPill} ${i === 0 ? styles.playerPillNew : ""} ${activePopup === player.alias ? styles.playerPillSelected : ""}`}
                  style={{animationDelay: `${i * 0.15}s`}}
                  onClick={() => handlePlayerClick(player.alias)}
                >
                  <span
                    className={styles.playerAvatar}
                    style={{borderColor: player.color}}
                  >
                    <img
                      src={player.avatar}
                      alt={player.name}
                      className={styles.playerAvatarImg}
                    />
                  </span>
                  {player.name}
                </button>
              ))}
            </div>
            {selectedPlayer && (
              <div
                className={styles.playerActionBar}
                ref={popupRef}
              >
                <div className={styles.actionBarPlayer}>
                  <img
                    src={selectedPlayer.avatar}
                    alt={selectedPlayer.name}
                    className={styles.actionBarAvatar}
                  />
                  <span className={styles.actionBarName}>{selectedPlayer.name}</span>
                </div>
                <div className={styles.actionBarBtns}>
                  <button
                    type="button"
                    className={styles.actionBarBtn}
                    onClick={() => {
                      setActivePopup(null);
                      navigate(`/u/${selectedPlayer.alias}`);
                    }}
                  >
                    👤 {t("promoViewProfile", {defaultValue: "Perfil"})}
                  </button>
                  <button
                    type="button"
                    className={styles.actionBarBtn}
                    onClick={() => {
                      setActivePopup(null);
                      navigate(`/u/${selectedPlayer.alias}`);
                    }}
                  >
                    <span className={styles.actionBarStoryDot} />
                    {t("promoViewStories", {defaultValue: "Historias"})}
                  </button>
                  <button
                    type="button"
                    className={styles.actionBarBtn}
                    onClick={() => {
                      setActivePopup(null);
                      navigate("/user/contacts");
                    }}
                  >
                    ➕ {t("promoAddContact", {defaultValue: "Añadir"})}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
